<template>
  <!-- Loading -->
  <div v-if="loading" class="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
    <div class="relative">
      <div class="animate-spin rounded-full h-16 w-16 border-4 border-slate-200"></div>
      <div class="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
    </div>
    <p class="mt-6 text-slate-500 font-medium">Cargando catálogo...</p>
  </div>

  <!-- No encontrada -->
  <div v-else-if="!config" class="min-h-screen flex items-center justify-center bg-slate-50 p-4">
    <div class="text-center max-w-md">
      <Icon icon="mdi:alert-circle" class="text-6xl text-red-500 mx-auto mb-4" />
      <h1 class="text-2xl font-bold text-slate-800 mb-2">Empresa no encontrada</h1>
      <p class="text-slate-500 mb-6">El tenant "{{ tenantId }}" no existe.</p>
      <router-link to="/login" class="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all">
        Ir al sistema
      </router-link>
    </div>
  </div>

  <!-- Catálogo -->
  <div v-else class="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex flex-col">
    
    <!-- Navbar -->
    <nav class="sticky top-0 w-full z-40 bg-white/80 backdrop-blur-xl shadow-md border-b border-slate-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16 md:h-20">
          
          <router-link :to="`/empresa/${tenantId}`" class="flex items-center gap-3">
            <img 
              :src="logoUrl" 
              :alt="config.nombreComercial || config.nombreEmpresa" 
              class="h-10 md:h-12 w-auto object-contain rounded-xl shadow-sm border border-slate-100"
              @error="handleLogoError"
            />
            <div class="flex flex-col">
              <span class="text-base md:text-lg font-black tracking-tight text-slate-900">
                {{ config.nombreComercial || config.nombreEmpresa }}
              </span>
              <span class="text-[9px] text-slate-400 font-bold uppercase tracking-wider -mt-1">
                Catálogo de Productos
              </span>
            </div>
          </router-link>

          <div class="hidden md:flex items-center space-x-2">
            <router-link 
              :to="`/empresa/${tenantId}`" 
              class="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:text-blue-600 hover:bg-blue-50/50 transition-all"
            >
              <Icon icon="mdi:home" class="inline mr-1" />
              Inicio
            </router-link>
            <a 
              v-if="whatsappNumber"
              :href="whatsappUrl" 
              target="_blank"
              class="px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 hover:shadow-xl transition-all shadow-md"
            >
              <Icon icon="mdi:whatsapp" class="inline mr-1" />
              WhatsApp
            </a>
          </div>

          <a 
            v-if="whatsappNumber"
            :href="whatsappUrl" 
            target="_blank"
            class="md:hidden px-4 py-2 rounded-xl text-xs font-bold bg-green-600 text-white"
          >
            <Icon icon="mdi:whatsapp" />
          </a>
        </div>
      </div>
    </nav>

    <!-- Hero del catálogo -->
    <section class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-10 md:py-14 px-4">
      <div class="max-w-7xl mx-auto text-center">
        <h1 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
          Catálogo de Productos
        </h1>
        <p class="text-slate-300 text-base md:text-lg mb-8">
          {{ config.nombreComercial || config.nombreEmpresa }} · {{ filteredProducts.length }} productos disponibles
        </p>

        <!-- Buscador -->
        <div class="max-w-2xl mx-auto">
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
            <input 
              v-model="searchTerm" 
              type="text" 
              placeholder="Buscar por nombre, código o categoría..."
              class="w-full pl-12 pr-12 py-4 rounded-2xl bg-white text-slate-900 border-0 focus:ring-4 focus:ring-blue-500/30 outline-none shadow-2xl"
              autocomplete="off"
            />
            <button 
              v-if="searchTerm" 
              @click="searchTerm = ''" 
              class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <Icon icon="mdi:close-circle" class="text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Filtros -->
    <section class="bg-white border-b border-slate-100 py-4 px-4 sticky top-16 md:top-20 z-30 shadow-sm">
      <div class="max-w-7xl mx-auto">
        <div class="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
          <button 
            @click="selectedCategory = ''" 
            :class="[
              'px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap shrink-0',
              selectedCategory === '' 
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            ]"
          >
            Todas ({{ products.length }})
          </button>
          <button 
            v-for="cat in categories" 
            :key="cat.id" 
            @click="selectedCategory = cat.id" 
            :class="[
              'px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap shrink-0',
              selectedCategory === cat.id 
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            ]"
          >
            {{ cat.nombre }} ({{ cat.count }})
          </button>
        </div>
      </div>
    </section>

    <!-- Grid de productos -->
    <main class="flex-1 py-8 px-4">
      <div class="max-w-7xl mx-auto">
        
        <!-- Sin resultados -->
        <div v-if="filteredProducts.length === 0" class="text-center py-20">
          <div class="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon icon="mdi:package-variant-remove" class="text-4xl text-slate-400" />
          </div>
          <h3 class="text-xl font-bold text-slate-700 mb-2">No se encontraron productos</h3>
          <p class="text-slate-500 mb-6">
            {{ searchTerm ? `No hay resultados para "${searchTerm}"` : 'No hay productos en esta categoría' }}
          </p>
          <button 
            v-if="searchTerm || selectedCategory" 
            @click="clearFilters"
            class="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
          >
            Limpiar filtros
          </button>
        </div>

        <!-- Grid -->
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div 
            v-for="product in paginatedProducts" 
            :key="product.idProducto" 
            @click="openModal(product)"
            class="group bg-white rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 border border-slate-100 flex flex-col cursor-pointer shadow-sm"
          >
            <!-- Imagen -->
            <div class="relative bg-gradient-to-br from-slate-50 to-blue-50/30 overflow-hidden" style="height:220px">
              <div class="w-full h-full flex items-center justify-center p-5">
                <img 
                  v-if="product.imagen" 
                  :src="getImageUrl(product.imagen)" 
                  :alt="product.nombre" 
                  class="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div v-else class="flex flex-col items-center justify-center text-slate-300">
                  <Icon icon="mdi:image-off" class="text-5xl opacity-40" />
                  <span class="text-xs font-semibold mt-2 opacity-60">Sin imagen</span>
                </div>
              </div>
              
              <!-- Badges -->
              <div class="absolute top-3 left-3">
                <span v-if="product.stock <= 0" class="bg-red-500 text-white text-[10px] uppercase font-bold px-2.5 py-1 rounded-full shadow-lg">
                  Agotado
                </span>
                <span v-else-if="product.stock < 5" class="bg-amber-500 text-white text-[10px] uppercase font-bold px-2.5 py-1 rounded-full shadow-lg">
                  Pocas unidades
                </span>
              </div>
            </div>
            
            <!-- Info -->
            <div class="flex flex-col flex-1 p-4 gap-3">
              <div class="text-center">
                <span class="inline-block text-[10px] font-bold text-blue-600 tracking-wider uppercase bg-blue-50 px-2.5 py-0.5 rounded-full">
                  {{ getCategoriaName(product) }}
                </span>
              </div>
              
              <h3 class="text-sm font-bold text-slate-800 text-center group-hover:text-blue-600 transition-colors overflow-hidden" style="display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;min-height:2.5rem">
                {{ product.nombre }}
              </h3>
              
              <p class="text-slate-400 text-[11px] text-center overflow-hidden" style="display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;min-height:2rem">
                {{ product.descripcion || 'Producto disponible con garantía.' }}
              </p>

              <div class="border-t border-slate-100"></div>

              <!-- Precio Contado -->
              <div class="text-center">
                <span class="text-[9px] text-slate-400 font-bold uppercase tracking-widest block">Precio Contado</span>
                <p class="text-2xl font-black text-slate-900 leading-tight">
                  ${{ formatPrice(product.precio_venta_usd || product.precioVentaUsd || 0) }}
                </p>
                <p v-if="tasaVenta > 0" class="text-[10px] font-semibold text-slate-400 mt-0.5">
                  ≈ {{ formatBsPrice(product.precio_venta_usd || product.precioVentaUsd || 0) }}
                </p>
              </div>

              <!-- Cashea -->
              <div class="bg-purple-50 border border-purple-100 rounded-xl p-2.5 text-center">
                <div class="flex items-center justify-center gap-1.5 mb-1">
                  <span class="w-1.5 h-1.5 rounded-full bg-purple-600 animate-pulse shrink-0"></span>
                  <span class="text-[9px] text-purple-800 font-extrabold uppercase tracking-widest">Cashea</span>
                </div>
                <p class="text-base font-black text-purple-700">
                  ${{ formatPrice((product.precio_venta_usd || product.precioVentaUsd || 0) * 1.17) }}
                </p>
                <p class="text-[9px] text-purple-400 font-semibold mt-0.5">+17% de recargo</p>
              </div>

              <!-- Botones -->
              <div class="flex flex-col gap-2 mt-auto" @click.stop>
                <a 
                  :href="getWhatsAppComprarUrl(product)" 
                  target="_blank" 
                  class="w-full flex items-center justify-center gap-1.5 py-2.5 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-xl text-xs font-bold hover:from-blue-600 hover:to-blue-700 transition-all shadow-md"
                >
                  <Icon icon="mdi:cart" />
                  Comprar en línea
                </a>
                <a 
                  :href="getWhatsAppConsultarUrl(product)" 
                  target="_blank" 
                  class="w-full flex items-center justify-center gap-1.5 py-2.5 bg-white text-green-700 border border-green-200 rounded-xl text-xs font-bold hover:bg-green-50 transition-all"
                >
                  <Icon icon="mdi:whatsapp" />
                  Consultar
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Paginación -->
        <div v-if="totalPages > 1" class="mt-12 flex justify-center">
          <div class="flex items-center gap-1 bg-white p-1.5 rounded-2xl shadow-lg border border-slate-100">
            <button 
              @click="currentPage--" 
              :disabled="currentPage === 1" 
              class="w-9 h-9 rounded-xl flex items-center justify-center transition-all"
              :class="currentPage === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 hover:text-blue-600'"
            >
              <Icon icon="mdi:chevron-left" />
            </button>
            
            <div class="hidden sm:flex gap-1 px-1">
              <button 
                v-for="page in visiblePages" 
                :key="page" 
                @click="currentPage = page" 
                class="w-9 h-9 rounded-xl font-bold text-sm transition-all"
                :class="currentPage === page 
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md' 
                  : 'text-slate-600 hover:bg-slate-100'"
              >
                {{ page }}
              </button>
            </div>
            
            <div class="sm:hidden px-3 text-sm font-semibold text-slate-600">
              {{ currentPage }} / {{ totalPages }}
            </div>
            
            <button 
              @click="currentPage++" 
              :disabled="currentPage === totalPages" 
              class="w-9 h-9 rounded-xl flex items-center justify-center transition-all"
              :class="currentPage === totalPages ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 hover:text-blue-600'"
            >
              <Icon icon="mdi:chevron-right" />
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- Modal Detalle -->
    <Teleport to="body">
      <Transition name="modal">
        <div
          v-if="selectedProduct"
          class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          @click.self="closeModal"
        >
          <div class="absolute inset-0 bg-slate-900/70 backdrop-blur-md" @click="closeModal"></div>

          <div class="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl z-10 flex flex-col md:flex-row overflow-hidden" style="max-height:90vh">
            
            <button
              @click="closeModal"
              class="absolute top-4 right-4 z-20 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-red-50 hover:text-red-500 transition-all text-slate-500"
            >
              <Icon icon="mdi:close" class="text-xl" />
            </button>

            <!-- Imagen -->
            <div class="md:w-5/12 bg-gradient-to-br from-slate-50 to-blue-50/50 flex items-center justify-center p-8 shrink-0">
              <img
                v-if="selectedProduct.imagen"
                :src="getImageUrl(selectedProduct.imagen)"
                :alt="selectedProduct.nombre"
                class="w-full object-contain drop-shadow-xl"
                style="max-height:360px"
              />
              <div v-else class="flex flex-col items-center text-slate-300">
                <Icon icon="mdi:image-off" class="text-7xl opacity-25" />
                <span class="text-sm font-semibold mt-3 opacity-40">Sin imagen</span>
              </div>
            </div>

            <div class="hidden md:block w-px bg-slate-100 shrink-0"></div>

            <!-- Info -->
            <div class="md:w-7/12 flex flex-col overflow-y-auto" style="max-height:90vh">
              <div class="p-6 flex flex-col gap-4">
                
                <div>
                  <span class="inline-block text-[10px] font-bold text-blue-600 tracking-wider uppercase bg-blue-50 px-3 py-1 rounded-full mb-2">
                    {{ getCategoriaName(selectedProduct) }}
                  </span>
                  <h2 class="text-xl font-black text-slate-900 leading-snug pr-10">{{ selectedProduct.nombre }}</h2>
                </div>

                <div>
                  <span v-if="selectedProduct.stock <= 0" class="inline-flex items-center gap-1.5 bg-red-50 text-red-600 text-xs font-bold px-3 py-1.5 rounded-full">
                    <span class="w-2 h-2 rounded-full bg-red-500"></span> Agotado
                  </span>
                  <span v-else-if="selectedProduct.stock < 5" class="inline-flex items-center gap-1.5 bg-amber-50 text-amber-600 text-xs font-bold px-3 py-1.5 rounded-full">
                    <span class="w-2 h-2 rounded-full bg-amber-500"></span> Últimas {{ selectedProduct.stock }} unidades
                  </span>
                  <span v-else class="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-600 text-xs font-bold px-3 py-1.5 rounded-full">
                    <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> En stock ({{ selectedProduct.stock }})
                  </span>
                </div>

                <div class="bg-slate-50 rounded-2xl p-4">
                  <h4 class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Descripción</h4>
                  <p class="text-sm text-slate-600 leading-relaxed">
                    {{ selectedProduct.descripcion || 'Producto disponible con garantía oficial. Contáctanos para más información.' }}
                  </p>
                </div>

                <div class="bg-gradient-to-r from-slate-50 to-slate-100/60 rounded-2xl p-4">
                  <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Precio Contado</span>
                  <p class="text-3xl font-black text-slate-900">
                    ${{ formatPrice(selectedProduct.precio_venta_usd || selectedProduct.precioVentaUsd || 0) }}
                  </p>
                  <p v-if="tasaVenta > 0" class="text-xs text-slate-500 font-semibold mt-1">
                    ≈ {{ formatBsPrice(selectedProduct.precio_venta_usd || selectedProduct.precioVentaUsd || 0) }}
                  </p>
                </div>

                <div class="bg-purple-50 border border-purple-100 rounded-2xl p-4">
                  <div class="flex items-center gap-2 mb-2">
                    <span class="w-2 h-2 rounded-full bg-purple-600 animate-pulse"></span>
                    <span class="text-[10px] text-purple-800 font-extrabold uppercase tracking-widest">Precio Cashea</span>
                  </div>
                  <p class="text-2xl font-black text-purple-700">
                    ${{ formatPrice((selectedProduct.precio_venta_usd || selectedProduct.precioVentaUsd || 0) * 1.17) }}
                  </p>
                  <p class="text-[10px] text-purple-400 font-semibold mt-1">Incluye 17% de recargo</p>
                </div>

                <div class="flex flex-col gap-2.5 pt-1 pb-2">
                  <a
                    :href="getWhatsAppComprarUrl(selectedProduct)"
                    target="_blank"
                    class="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-xl text-sm font-bold hover:from-blue-600 hover:to-blue-700 transition-all shadow-md"
                  >
                    <Icon icon="mdi:cart" class="text-lg" />
                    Comprar Ahora
                  </a>
                  <a
                    :href="getWhatsAppConsultarUrl(selectedProduct)"
                    target="_blank"
                    class="w-full flex items-center justify-center gap-2 py-3.5 bg-white text-green-700 border-2 border-green-200 rounded-xl text-sm font-bold hover:bg-green-50 transition-all"
                  >
                    <Icon icon="mdi:whatsapp" class="text-lg" />
                    Consultar por WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Footer -->
    <footer class="bg-slate-950 text-slate-400 py-6 px-4">
      <div class="max-w-7xl mx-auto text-center">
        <p class="text-xs">
          &copy; {{ currentYear }} {{ config.nombreComercial || config.nombreEmpresa }}
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { obtenerProductos, obtenerUrlImagen } from '@/api/productos.js';
import { obtenerCategorias } from '@/api/categorias.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { obtenerConfigEmpresa } from '@/api/empresa.js';
import { getAssetUrl } from '@/utils/tenantConfig';

export default {
  name: 'TenantCatalogo',
  components: { Icon },
  props: {
    tenantId: { 
      type: String, 
      required: true 
    }
  },
  data() {
    return {
      loading: true,
      config: null,
      logoError: false,
      
      products: [],
      categories: [],
      tasaVenta: 0,
      
      searchTerm: '',
      selectedCategory: '',
      currentPage: 1,
      itemsPerPage: 12,
      
      selectedProduct: null
    };
  },
  computed: {
    logoUrl() {
      if (this.logoError) return this.getFallbackLogo();
      if (this.config?.logoBase64) return this.config.logoBase64;
      if (this.config?.logoUrl) return getAssetUrl(this.config.logoUrl);
      return this.getFallbackLogo();
    },
    whatsappNumber() {
      return (this.config?.whatsapp || this.config?.telefono1 || '').replace(/\D/g, '');
    },
    whatsappUrl() {
      if (!this.whatsappNumber) return '#';
      return `https://wa.me/${this.whatsappNumber}`;
    },
    filteredProducts() {
      let result = [...this.products];
      
      if (this.selectedCategory) {
        result = result.filter(p => String(p.idCategoria) === String(this.selectedCategory));
      }
      
      if (this.searchTerm) {
        const q = this.searchTerm.toLowerCase().trim();
        result = result.filter(p => 
          (p.nombre || '').toLowerCase().includes(q) ||
          (p.codigo || '').toLowerCase().includes(q) ||
          (p.marca || '').toLowerCase().includes(q) ||
          (p.modelo || '').toLowerCase().includes(q) ||
          (p.descripcion || '').toLowerCase().includes(q)
        );
      }
      
      return result;
    },
    totalPages() {
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    },
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredProducts.slice(start, start + this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    currentYear() {
      return new Date().getFullYear();
    }
  },
  watch: {
    searchTerm() {
      this.currentPage = 1;
    },
    selectedCategory() {
      this.currentPage = 1;
    }
  },
  async mounted() {
    await this.cargarConfig();
    await this.cargarDatos();
    this.adjustItemsPerPage();
    window.addEventListener('resize', this.adjustItemsPerPage);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.adjustItemsPerPage);
  },
  methods: {
    getFallbackLogo() {
      const inicial = (this.config?.nombreComercial || this.config?.nombreEmpresa || 'S').charAt(0).toUpperCase();
      return `data:image/svg+xml;utf8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <rect width="100" height="100" rx="20" fill="#3b82f6"/>
          <text x="50" y="68" font-size="55" font-weight="bold" fill="white" text-anchor="middle" font-family="Arial">${inicial}</text>
        </svg>
      `)}`;
    },
    async cargarConfig() {
      try {
        const res = await obtenerConfigEmpresa(this.tenantId);
        if (res.success && res.data) {
          this.config = res.data;
          document.title = `Catálogo - ${this.config.nombreComercial || this.config.nombreEmpresa}`;
        } else {
          this.config = null;
        }
      } catch (e) {
        console.error('Error cargando config:', e);
        this.config = null;
      }
    },
    async cargarDatos() {
      this.loading = true;
      try {
        // ⚠️ IMPORTANTE: El backend debe filtrar por tenantId
        // Aquí asumimos que el tenant se pasa automáticamente vía headers/auth
        const [prodRes, catRes, tasaRes] = await Promise.all([
          obtenerProductos('activos'),
          obtenerCategorias('activas'),
          obtenerTasaVentaActual()
        ]);
        
        if (prodRes.success) {
          this.products = prodRes.data || [];
        }
        
        if (catRes.success) {
          this.categories = (catRes.data || []).map(c => ({
            id: c.idCategoria,
            nombre: c.nombre,
            count: this.products.filter(p => String(p.idCategoria) === String(c.idCategoria)).length
          })).filter(c => c.count > 0);
        }
        
        if (tasaRes.success && tasaRes.data) {
          this.tasaVenta = tasaRes.data.precio_usd_bs || 0;
        }
      } catch (e) {
        console.error('Error cargando datos:', e);
      } finally {
        this.loading = false;
      }
    },
    adjustItemsPerPage() {
      const w = window.innerWidth;
      if (w < 480) this.itemsPerPage = 6;
      else if (w < 768) this.itemsPerPage = 8;
      else if (w < 1024) this.itemsPerPage = 9;
      else this.itemsPerPage = 12;
    },
    clearFilters() {
      this.searchTerm = '';
      this.selectedCategory = '';
    },
    getImageUrl(img) {
      try {
        return obtenerUrlImagen(img);
      } catch {
        return 'https://placehold.co/600x400/f1f5f9/94a3b8?text=Sin+Imagen';
      }
    },
    getCategoriaName(product) {
      if (product.nombreCategoria) return product.nombreCategoria;
      const cat = this.categories.find(c => String(c.id) === String(product.idCategoria));
      return cat?.nombre || 'General';
    },
    formatPrice(value) {
      return Number(value || 0).toFixed(2);
    },
    formatBsPrice(priceUsd) {
      if (!this.tasaVenta) return '';
      const total = Number(priceUsd || 0) * this.tasaVenta;
      return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(total) + ' Bs';
    },
    getWhatsAppConsultarUrl(product) {
      const num = this.whatsappNumber || '584125696219';
      const text = encodeURIComponent(`Hola ${this.config?.nombreComercial || ''}. Estoy interesado en: *${product.nombre}* (Cod: ${product.codigo || 'N/A'}).`);
      return `https://wa.me/${num}?text=${text}`;
    },
    getWhatsAppComprarUrl(product) {
      const num = this.whatsappNumber || '584125696219';
      const price = this.formatPrice(product.precio_venta_usd || product.precioVentaUsd || 0);
      const text = encodeURIComponent(`Hola ${this.config?.nombreComercial || ''}. Quiero COMPRAR: *${product.nombre}* (Cod: ${product.codigo || 'N/A'}) por $${price}.`);
      return `https://wa.me/${num}?text=${text}`;
    },
    openModal(product) {
      this.selectedProduct = product;
      document.body.style.overflow = 'hidden';
    },
    closeModal() {
      this.selectedProduct = null;
      document.body.style.overflow = '';
    },
    handleLogoError() {
      this.logoError = true;
    }
  }
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }

.scrollbar-thin::-webkit-scrollbar { height: 4px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
</style>